import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from "@chakra-ui/react";
import { EventDetailSummarySection, KYDEvent } from "@common/interfaces/KYDEvent";
import { yupResolver } from "@hookform/resolvers/yup";
import React, { Suspense } from "react";
import { FormProvider, useForm } from "react-hook-form";
import * as y from "yup";

const VenueMap = React.lazy(() => import("./VenueMap"));

const EditVenueSchema = y.object({
  mapbox_id: y.string().optional(),
  venue_name: y.string().required(),
  venue_address: y.string().required(),
  lat: y.number().optional(),
  lng: y.number().optional(),
});

export type EditVenueValues = y.InferType<typeof EditVenueSchema>;

const EditVenueModal = ({
  onClose,
  onSave,
  isLoading = false,
  isOpen = false,
  data,
}: {
  onClose: () => void;
  onSave: (data: EditVenueValues) => void;
  isLoading: boolean;
  isOpen: boolean;
  data: EventDetailSummarySection["data"]
}) => {
  const venue = data.venue;
  const hasVenue = !!venue;

  const defaultValues = hasVenue
    ? {
        mapbox_id: venue.mapbox_id,
        venue_address: venue.address,
        venue_name: venue.name,
        lng: venue.location?.Geometry?.Point[0],
        lat: venue.location?.Geometry?.Point[1],
      }
    : {
        mapbox_id: undefined,
        venue_address: "",
        venue_name: "",
        lng: undefined,
        lat: undefined,
      };

  const methods = useForm<EditVenueValues>({
    resolver: yupResolver(EditVenueSchema),
    mode: "onChange",
    criteriaMode: "all",
    values: defaultValues,
  });

  const {
    register,
    formState: { isValid, isDirty },
    handleSubmit,
    reset,
    watch,
  } = methods;

  const onSubmit = (data) => {
    onSave(data);
  };

  const _onClose = () => {
    reset(defaultValues);
    onClose();
  };

  const venue_address = watch("venue_address");

  return (
    <Modal size={"3xl"} isOpen={isOpen} onClose={_onClose} closeOnOverlayClick>
      <ModalOverlay />
      <ModalContent>
        <FormProvider {...methods}>
          <ModalHeader>Edit Venue</ModalHeader>
          <ModalBody>
            <Stack spacing={4}>
              <Suspense fallback={<span>Loading...</span>}>
                <VenueMap
                  mapboxAccessToken={
                    //@ts-ignore
                    data.mapbox?.publishable_key
                  }
                  isLoading={isLoading}
                />
              </Suspense>
              {venue_address && venue_address.length > 0 ? (
                <Stack spacing={0}>
                  <Text fontWeight={"medium"}>Venue Address</Text>
                  <Text>{venue_address}</Text>
                </Stack>
              ) : null}
              ;
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button
              isDisabled={isLoading}
              onClick={() => {
                _onClose();
              }}
              mr="4"
            >
              Cancel
            </Button>
            <Button
              color="white"
              bg="green.500"
              isLoading={isLoading}
              isDisabled={!isValid || !isDirty}
              onClick={() => {
                handleSubmit(onSubmit)();
              }}
            >
              Save
            </Button>
          </ModalFooter>
        </FormProvider>
      </ModalContent>
    </Modal>
  );
};

export default EditVenueModal;
